import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, getDocsFromServer, updateDoc, addDoc, Timestamp, onSnapshot, query, orderBy, deleteDoc, type QuerySnapshot, type DocumentData } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { Event, RSVP, Invoice, UnitMembership } from '@/types';
import { formatDate, formatCurrency } from '@/lib/utils';
import { Calendar, MapPin, Users, PoundSterling, Settings, FileText, LayoutGrid, CheckCircle, Clock, Download, Printer, ChefHat, ClipboardList, Bell, BarChart3, Copy, UserPlus, Trash2, User, Shield, Wallet, Lock, Unlock } from 'lucide-react';
import { showSuccess, showError, confirmAction } from '@/lib/swal';
import {
  generatePlaceCards,
  exportToCSV,
} from '@/lib/pdf-generator';
import PublishEventModal from '@/components/event/PublishEventModal';
import RSVPLinkModal from '@/components/event/RSVPLinkModal';
import { EventHeaderCredits } from '@/components/event/EventHeaderCredits';
import { useAuth } from '@/contexts/AuthContext';
import { useUnit } from '@/contexts/UnitContext';
import { mapEventData, mapRsvpData, getRsvpExpectedAmount, formatEventStatusLabel, isActiveInvoiceData, computeRsvpSubtotalForInvoice, buildAttendeeStatSummary, resolveCurrentAttendeesSyncTarget } from '@/lib/event-utils';
import { getAttendanceRsvpPaymentState, getInvoicePayerRsvp } from '@/lib/attendance-payment-state';
import { ensureInvoiceForBooking, mapFirestoreInvoiceData } from '@/lib/ensure-invoice-for-rsvp';
import {
  computeInvoicePolicyDisplayTotal,
  getHeadlinePayableDueForDashboard,
} from '@/lib/invoice-payment-breakdown';
import { InvoicePaymentsModal } from '@/components/event/InvoicePaymentsModal';
import { normalizeTicketCoursesToFormatA, getSelectionFromFormatA } from '@/lib/ticket-courses';
import { isMultiTenantEvent } from '@/lib/ticket-mode';
import { 
  TEST_FIRST_NAMES,
  TEST_LAST_NAMES,
  TEST_ADDITIONAL_RANKS,
  TEST_UNIT_NAMES,
  TEST_ALLERGIES,
  TEST_MEAL_CHOICES,
  TEST_EMAIL_DOMAINS,
} from '@/lib/test-data';

const RSVP_UNIT_MAX_LEN = 18;
const RSVP_MEAL_MAX_LEN = 40;

type RsvpDashboardRowModel = {
  isInterestOnly: boolean;
  compactMealSummary: string;
  mealTooltip: string | undefined;
  unitDisplay: string;
  unitTruncated: string;
  hasMeaningfulNote: boolean;
};

export function buildRsvpDashboardRowModel(rsvp: RSVP, ev: Event): RsvpDashboardRowModel {
  const isInterestOnly = ev.eventStatus === 'interest-only' && rsvp.status === 'waitlist';

  let compactMealSummary = 'No meal selected';
  let mealTooltip: string | undefined;

  if (!isInterestOnly) {
    if (ev?.tickets && ev.tickets.length > 0 && Array.isArray(rsvp.responses?.Tickets)) {
      const ticketEntries = (rsvp.responses.Tickets as any[]).filter((t) => t && t.qty > 0);
      const multiTenant = isMultiTenantEvent(ev);
      if (ticketEntries.length > 0) {
        const parts: string[] = [];
        const tooltipParts: string[] = [];
        const normalizedTc = normalizeTicketCoursesToFormatA((rsvp.responses as any)?.TicketCourses);
        ticketEntries.forEach((ticket) => {
          const ticketType = ev.tickets?.find((tt) => tt.id === ticket.id);
          const label = ticketType?.name ?? (ticket.name as string) ?? 'Ticket';
          parts.push(`${label} ×${ticket.qty}`);
          if (multiTenant) return;
          const choices: string[] = [];
          if (ticketType?.courses) {
            for (let i = 1; i <= ticket.qty; i++) {
              ticketType.courses.forEach((course) => {
                const sel = getSelectionFromFormatA(normalizedTc, ticket.id, course.id, i);
                if (sel) choices.push(sel);
              });
            }
          }
          if (choices.length > 0) tooltipParts.push(`${label}: ${choices.join(', ')}`);
        });
        compactMealSummary = parts.join(', ');
        if (tooltipParts.length > 0) mealTooltip = tooltipParts.join(' | ');
      }
    } else if (ev?.courses && ev.courses.length > 0) {
      const selections = ev.courses.map((course) => rsvp.responses?.[course.name]).filter(Boolean);
      if (selections.length > 0) {
        compactMealSummary = selections.join(', ');
        if (compactMealSummary.length > RSVP_MEAL_MAX_LEN) {
          mealTooltip = compactMealSummary;
          compactMealSummary = compactMealSummary.slice(0, RSVP_MEAL_MAX_LEN) + '…';
        }
      }
    } else if (rsvp.responses?.Meal) {
      compactMealSummary = rsvp.responses.Meal;
      if (compactMealSummary.length > RSVP_MEAL_MAX_LEN) {
        mealTooltip = compactMealSummary;
        compactMealSummary = compactMealSummary.slice(0, RSVP_MEAL_MAX_LEN) + '…';
      }
    }
  }

  const hasMeaningfulNote =
    !!rsvp.specialRequests?.trim() && rsvp.specialRequests.trim().toLowerCase() !== 'none';
  const unitDisplay = rsvp.unit || '-';
  const unitTruncated =
    unitDisplay.length > RSVP_UNIT_MAX_LEN ? unitDisplay.slice(0, RSVP_UNIT_MAX_LEN) + '…' : unitDisplay;

  return {
    isInterestOnly,
    compactMealSummary,
    mealTooltip,
    unitDisplay,
    unitTruncated,
    hasMeaningfulNote,
  };
}

export default function EventDashboardPage() {
  const { eventId, unitId } = useParams<{ eventId: string; unitId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [invoiceMap, setInvoiceMap] = useState<Record<string, Invoice>>({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalInvoice, setPaymentModalInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Helper to check if user is admin for detailed logging
  const isAdmin = userProfile?.isAdmin === true;
  
  // Admin-only detailed logging helper
  const adminLog = (...args: any[]) => {
    if (isAdmin) {
      console.log('[ADMIN DEBUG - Event Dashboard]', ...args);
    }
  };
  
  const adminWarn = (...args: any[]) => {
    if (isAdmin) {
      console.warn('[ADMIN DEBUG - Event Dashboard]', ...args);
    }
  };
  
  const adminError = (...args: any[]) => {
    if (isAdmin) {
      console.error('[ADMIN DEBUG - Event Dashboard]', ...args);
    }
  };
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [addRandomRsvpsOpen, setAddRandomRsvpsOpen] = useState(false);
  const [numRandomRsvps, setNumRandomRsvps] = useState('10');
  const [generatingRsvps, setGeneratingRsvps] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rsvpLinkModalOpen, setRsvpLinkModalOpen] = useState(false);
  const [isAdminViewing, setIsAdminViewing] = useState(false);
  const { units } = useUnit();
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'payment'>('recent');
  const displayedRsvps = useMemo(() => {
    adminLog('=== DISPLAYED RSVPs CALCULATION ===');
    adminLog('Total RSVPs:', rsvps.length);
    adminLog('Sort by:', sortBy);
    adminLog('Event status:', event?.eventStatus);
    
    // In waitlist mode (interest-only), only show waitlist RSVPs
    let list = [...rsvps];
    if (event?.eventStatus === 'interest-only') {
      list = list.filter(rsvp => rsvp.status === 'waitlist');
      adminLog('Filtered to waitlist RSVPs:', list.length);
    }
    
    if (sortBy === 'name') {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      adminLog('Sorted by name');
    } else if (sortBy === 'payment' && event) {
      list.sort((a, b) => {
        const pa = getAttendanceRsvpPaymentState(a, rsvps, event, invoiceMap).displayPaid ? 1 : 0;
        const pb = getAttendanceRsvpPaymentState(b, rsvps, event, invoiceMap).displayPaid ? 1 : 0;
        return pb - pa;
      });
      adminLog('Sorted by payment status');
    } else {
      adminLog('Using default sort (recent)');
    }
    
    adminLog('Displayed RSVPs count:', list.length);
    return list;
  }, [rsvps, sortBy, event, invoiceMap, isAdmin]);

  const rsvpMap = useMemo(() => {
    const map = new Map<string, RSVP>();
    rsvps.forEach((rsvp) => map.set(rsvp.id, rsvp));
    return map;
  }, [rsvps]);

  const attendeeStatSummary = useMemo(() => buildAttendeeStatSummary(rsvps), [rsvps]);

  // Calculate ticket price for an RSVP (custom line prices and customPrice override included)
  const getRsvpTicketPrice = (rsvp: RSVP, eventData: Event | null): number => {
    if (!eventData) return 0;
    return computeRsvpSubtotalForInvoice(rsvp, eventData);
  };

  // Calculate revenue and outstanding based on invoices and tickets
  const revenueStats = useMemo(() => {
    adminLog('=== REVENUE STATS CALCULATION ===');
    adminLog('RSVPs count:', rsvps.length);
    adminLog('Invoices count:', Object.keys(invoiceMap).length);
    adminLog('Event:', event ? { id: event.id, name: event.name } : 'null');

    let totalCollected = 0;   // sum of amountPaid (money actually received)
    let totalOutstanding = 0; // genuinely unpaid (excludes pending_verification)
    let totalPending = 0;     // claimed via bank transfer, awaiting approval
    let totalTicketValue = 0; // total billed / expected across all non-voided invoices
    let rsvpCount = 0;
    let paidRsvpCount = 0;
    let pendingRsvpCount = 0;
    let unpaidRsvpCount = 0;

    // We'll track which RSVPs have been accounted for via invoices
    const accountedRsvpIds = new Set<string>();

    // 1. Process all invoices to get real financial totals
    const breakdown = event && rsvps.length ? { event, rsvps } : null;

    Object.values(invoiceMap).forEach((inv) => {
      if (inv.status === 'voided') return;

      const paid = Number(inv.amountPaid) || 0;
      const headlineDue =
        breakdown && Array.isArray(inv.rsvps) && inv.rsvps.length
          ? getHeadlinePayableDueForDashboard(inv, breakdown)
          : Math.max(0, Number(inv.outstandingBalance) || 0);
      const policyTotal =
        breakdown && event && Array.isArray(inv.rsvps) && inv.rsvps.length
          ? computeInvoicePolicyDisplayTotal(inv, event, rsvps)
          : Number(inv.totalAmount) || 0;

      totalCollected += paid;
      totalTicketValue += policyTotal;

      const rsvpsOnInvoice = Array.isArray(inv.rsvps) ? inv.rsvps.length : 0;
      if (inv.status === 'pending_verification') {
        totalPending += headlineDue;
        pendingRsvpCount += rsvpsOnInvoice;
      } else {
        totalOutstanding += headlineDue;
        if (headlineDue <= 0) {
          paidRsvpCount += rsvpsOnInvoice;
        } else {
          unpaidRsvpCount += rsvpsOnInvoice;
        }
      }

      // Track which RSVPs are linked to this invoice
      if (Array.isArray(inv.rsvps)) {
        inv.rsvps.forEach((id) => accountedRsvpIds.add(id));
      }
    });

    // 2. For RSVPs without an invoice, use ticket prices as fallback
    rsvps.forEach((rsvp) => {
      if (rsvp.apologies || rsvp.status === 'cancelled') return;

      if (accountedRsvpIds.has(rsvp.id)) {
        rsvpCount++;
        return;
      }

      const ticketPrice = getRsvpTicketPrice(rsvp, event);
      totalTicketValue += ticketPrice;
      rsvpCount++;

      if (rsvp.paid) {
        totalCollected += ticketPrice;
        paidRsvpCount++;
      } else {
        totalOutstanding += ticketPrice;
        unpaidRsvpCount++;
      }
    });

    const averagePrice = rsvpCount > 0 ? totalTicketValue / rsvpCount : 0;

    const stats = {
      totalRevenue: totalTicketValue,   // total billed / expected
      totalCollected,                   // actual money received
      totalOutstanding,                 // genuinely unpaid
      totalPending,                     // pending_verification
      averagePrice,
      paidRsvpCount,
      pendingRsvpCount,
      unpaidRsvpCount,
    };

    adminLog('Revenue stats result (from Invoices + RSVPs):', stats);
    adminLog('RSVP count:', rsvpCount, 'Total ticket value (including fees):', totalTicketValue);

    return stats;
  }, [rsvps, event, invoiceMap, isAdmin]);

  useEffect(() => {
    if (!event?.unitId || !event?.id) {
      setInvoiceMap({});
      return;
    }
    const uid = event.unitId;
    const eid = event.id;
    const invoicesCol = collection(db, 'units', uid, 'events', eid, 'invoices');

    const applyInvoiceSnapshot = (snapshot: QuerySnapshot<DocumentData>) => {
      if (cancelled) return;
      const map: Record<string, Invoice> = {};
      snapshot.docs.forEach((snap) => {
        const d = snap.data();
        if (!isActiveInvoiceData(d)) {
          return;
        }
        map[snap.id] = mapFirestoreInvoiceData(snap.id, d as Record<string, unknown>);
      });
      setInvoiceMap(map);
    };

    let cancelled = false;

    getDocsFromServer(invoicesCol)
      .then((snapshot) => {
        if (!cancelled) applyInvoiceSnapshot(snapshot);
      })
      .catch(() => {
        // Offline: listener still applies cached or later server data.
      });

    const unsub = onSnapshot(invoicesCol, applyInvoiceSnapshot);
    return () => {
      cancelled = true;
      unsub();
    };
  }, [event?.unitId, event?.id]);

  useEffect(() => {
    let unsubscribeEvent: (() => void) | null = null;
    let unsubscribeRsvps: (() => void) | null = null;

    const handleRealtimeError = (scope: 'event' | 'rsvps', err: unknown) => {
      adminError(`Realtime listener failed (${scope}):`, err);
      console.error(`[Event Dashboard] Realtime listener (${scope}):`, err);
      setError(
        'Real-time updates failed. Check your connection and refresh the page if the problem continues.',
      );
    };

    const init = async () => {
      if (!eventId) {
        adminWarn('No eventId provided');
        return;
      }
      
      adminLog('=== EVENT DASHBOARD INIT START ===');
      adminLog('Event ID:', eventId);
      adminLog('User Profile:', userProfile);
      adminLog('Is Admin:', isAdmin);
      adminLog('Units available:', units.length, units.map(u => ({ id: u.id, name: u.name })));
      
      setLoading(true);
      setError(null);

      try {
        adminLog('Searching for event across units...');
        let eventFound = false;
        
        // If unitId is provided in URL, try that first (optimization)
        if (unitId) {
          adminLog('Unit ID provided in URL, checking unit:', unitId);
          const eventRef = doc(db, 'units', unitId, 'events', eventId);
          const eventSnap = await getDoc(eventRef);
          
          if (eventSnap.exists()) {
            const eventData = eventSnap.data();
            
            adminLog('Event found in unit from URL:', unitId);
            
            // Check if user has access to this unit (or is admin)
            const hasAccess = units.some(u => u.id === unitId) || isAdmin;
            
            if (hasAccess) {
              unsubscribeEvent = onSnapshot(
                eventRef,
                (snap) => {
                  if (snap.exists()) {
                    const mappedEvent = mapEventData(snap, unitId);
                    setEvent(mappedEvent);
                  } else {
                    adminWarn('Event document no longer exists');
                  }
                },
                (err) => handleRealtimeError('event', err),
              );

              const rsvpsRef = collection(db, 'units', unitId, 'events', eventId, 'rsvps');
              unsubscribeRsvps = onSnapshot(
                query(rsvpsRef, orderBy('createdAt', 'desc')),
                (snapshot) => {
                  const rows: RSVP[] = [];
                  snapshot.docs.forEach((d) => {
                    const mappedRsvp = mapRsvpData(d as any);
                    if (!mappedRsvp) return;
                    mappedRsvp.eventId = eventId;
                    rows.push(mappedRsvp);
                  });
                  setRsvps(rows);
                },
                (err) => handleRealtimeError('rsvps', err),
              );

              setIsAdminViewing(isAdmin && !units.some(u => u.id === unitId));
              eventFound = true;
              setLoading(false);
              adminLog('Event loaded from URL unit ID');
              return;
            } else {
              adminLog('User does not have access to unit from URL, will search normally');
            }
          }
        }
        
        // First, search in user's units
        for (const u of units) {
          adminLog('Checking unit:', u.id, u.name);
          const eventRef = doc(db, 'units', u.id, 'events', eventId);
          const eventSnap = await getDoc(eventRef);
          
          adminLog('Event document exists:', eventSnap.exists(), 'in unit:', u.id);
          
          if (eventSnap.exists()) {
            const unitId = u.id;
            const eventData = eventSnap.data();
            
            adminLog('Event found in unit:', unitId);
            adminLog('Event data:', {
              id: eventId,
              name: eventData.name,
              date: eventData.date?.toDate()?.toISOString(),
              eventStatus: eventData.eventStatus,
              currentAttendees: eventData.currentAttendees,
              maxCapacity: eventData.maxCapacity,
            });

            unsubscribeEvent = onSnapshot(
              eventRef,
              (snap) => {
                if (snap.exists()) {
                  const mappedEvent = mapEventData(snap, unitId);
                  adminLog('Event snapshot updated:', {
                    id: mappedEvent.id,
                    name: mappedEvent.name,
                    date: mappedEvent.date?.toISOString(),
                    eventStatus: mappedEvent.eventStatus,
                    currentAttendees: mappedEvent.currentAttendees,
                  });
                  setEvent(mappedEvent);
                } else {
                  adminWarn('Event document no longer exists');
                }
              },
              (err) => handleRealtimeError('event', err),
            );

            const rsvpsRef = collection(db, 'units', unitId, 'events', eventId, 'rsvps');
            adminLog('Setting up RSVPs snapshot listener for path:', rsvpsRef.path);
            
            unsubscribeRsvps = onSnapshot(query(rsvpsRef, orderBy('createdAt', 'desc')), (snapshot) => {
              adminLog('=== RSVPs SNAPSHOT UPDATE ===');
              adminLog('RSVPs snapshot size:', snapshot.size);
              adminLog('RSVPs snapshot empty:', snapshot.empty);
              adminLog('RSVPs snapshot metadata:', {
                hasPendingWrites: snapshot.metadata.hasPendingWrites,
                fromCache: snapshot.metadata.fromCache,
              });
              
              const rows: RSVP[] = [];
              snapshot.docs.forEach((d, index) => {
                const rawData = d.data();
                const mappedRsvp = mapRsvpData(d as any);
                if (!mappedRsvp) return;
                mappedRsvp.eventId = eventId;
                
                adminLog(`RSVP ${index + 1}/${snapshot.size}:`, {
                  id: mappedRsvp.id,
                  name: mappedRsvp.name,
                  email: mappedRsvp.email,
                  userId: mappedRsvp.userId,
                  status: mappedRsvp.status,
                  paid: mappedRsvp.paid,
                  apologies: mappedRsvp.apologies,
                  eventId: mappedRsvp.eventId,
                  createdAt: mappedRsvp.createdAt?.toISOString(),
                  updatedAt: mappedRsvp.updatedAt?.toISOString(),
                  invoiceId: mappedRsvp.invoiceId,
                  rawData: {
                    status: rawData.status,
                    paid: rawData.paid,
                    userId: rawData.userId,
                    email: rawData.email,
                    eventId: rawData.eventId,
                  },
                });
                
                rows.push(mappedRsvp);
              });
              
              adminLog('Total RSVPs processed:', rows.length);
              adminLog('RSVPs by status:', {
                confirmed: rows.filter(r => r.status === 'confirmed').length,
                pending: rows.filter(r => r.status === 'pending').length,
                waitlist: rows.filter(r => r.status === 'waitlist').length,
                declined: rows.filter(r => r.status === 'declined').length,
                cancelled: rows.filter(r => r.status === 'cancelled').length,
                noStatus: rows.filter(r => !r.status).length,
              });
              adminLog('RSVPs by payment:', {
                paid: rows.filter(r => r.paid || getRsvpExpectedAmount(r, event) === 0).length,
                unpaid: rows.filter(r => !r.paid && getRsvpExpectedAmount(r, event) > 0).length,
              });
              
              setRsvps(rows);
            }, (err) => handleRealtimeError('rsvps', err));

            adminLog('Event and RSVPs listeners set up successfully');
            setIsAdminViewing(false); // User is a member of this unit
            eventFound = true;
            break;
          }
        }

        // If event not found in user's units and user is admin, search all units
        if (!eventFound && isAdmin) {
          adminLog('Event not found in user units, searching all units (admin access)...');
          const unitsSnapshot = await getDocs(collection(db, 'units'));
          
          for (const unitDoc of unitsSnapshot.docs) {
            const unitId = unitDoc.id;
            adminLog('Checking unit:', unitId, unitDoc.data().name);
            const eventRef = doc(db, 'units', unitId, 'events', eventId);
            const eventSnap = await getDoc(eventRef);
            
            if (eventSnap.exists()) {
              const eventData = eventSnap.data();
              
              adminLog('Event found in unit (admin access):', unitId);
              adminLog('Event data:', {
                id: eventId,
                name: eventData.name,
                date: eventData.date?.toDate()?.toISOString(),
                eventStatus: eventData.eventStatus,
                currentAttendees: eventData.currentAttendees,
                maxCapacity: eventData.maxCapacity,
              });

              unsubscribeEvent = onSnapshot(
                eventRef,
                (snap) => {
                  if (snap.exists()) {
                    const mappedEvent = mapEventData(snap, unitId);
                    adminLog('Event snapshot updated:', {
                      id: mappedEvent.id,
                      name: mappedEvent.name,
                      date: mappedEvent.date?.toISOString(),
                      eventStatus: mappedEvent.eventStatus,
                      currentAttendees: mappedEvent.currentAttendees,
                    });
                    setEvent(mappedEvent);
                  } else {
                    adminWarn('Event document no longer exists');
                  }
                },
                (err) => handleRealtimeError('event', err),
              );

              const rsvpsRef = collection(db, 'units', unitId, 'events', eventId, 'rsvps');
              adminLog('Setting up RSVPs snapshot listener for path:', rsvpsRef.path);
              
              unsubscribeRsvps = onSnapshot(
                query(rsvpsRef, orderBy('createdAt', 'desc')),
                (snapshot) => {
                  adminLog('=== RSVPs SNAPSHOT UPDATE ===');
                  adminLog('RSVPs snapshot size:', snapshot.size);

                  const rows: RSVP[] = [];
                  snapshot.docs.forEach((d) => {
                    const mappedRsvp = mapRsvpData(d as any);
                    if (!mappedRsvp) return;
                    mappedRsvp.eventId = eventId;
                    rows.push(mappedRsvp);
                  });

                  setRsvps(rows);
                },
                (err) => handleRealtimeError('rsvps', err),
              );

              adminLog('Event and RSVPs listeners set up successfully (admin access)');
              setIsAdminViewing(true); // Admin viewing event from unit they're not a member of
              eventFound = true;
              break;
            }
          }
        }

        if (!eventFound) {
          adminWarn('Event not found in any accessible unit');
          setError('Event not found');
          setIsAdminViewing(false);
        }
      } catch (e: any) {
        adminError('Error initializing event listeners:', e);
        console.error('Error initializing event listeners:', e);
        setError('Failed to load event');
      } finally {
        setLoading(false);
        adminLog('=== EVENT DASHBOARD INIT END ===');
      }
    };

    init();

    return () => {
      adminLog('Cleaning up event dashboard listeners');
      if (unsubscribeEvent) unsubscribeEvent();
      if (unsubscribeRsvps) unsubscribeRsvps();
    };
  }, [eventId, unitId, units, isAdmin]);

  // Keep event.currentAttendees aligned with live RSVP data (fixes drift from legacy paths).
  useEffect(() => {
    if (!event?.unitId || !event.id || event.eventStatus === 'interest-only') {
      return;
    }
    const syncTarget = resolveCurrentAttendeesSyncTarget(
      rsvps,
      event.currentAttendees,
      event.eventStatus,
    );
    if (syncTarget === null) {
      return;
    }
    const eventRef = doc(db, 'units', event.unitId, 'events', event.id);
    updateDoc(eventRef, { currentAttendees: syncTarget }).catch((err) => {
      console.error('Failed to sync currentAttendees', err);
    });
  }, [event?.id, event?.unitId, event?.currentAttendees, event?.eventStatus, rsvps]);

  const handlePublishEvent = () => {
    setPublishModalOpen(true);
  };

  const handleEventPublished = (status: 'interest-only' | 'published') => {
    if (event) {
      setEvent({ ...event, eventStatus: status });
    }
  };

  const handleUnpublishEvent = async () => {
    if (!event) return;

    const result = await confirmAction({
      title: 'Unpublish Event?',
      text: 'This will revert the event to draft status. Users will no longer be able to access the RSVP page. Existing RSVPs will be preserved.',
      confirmButtonText: 'Unpublish',
    });

    if (!result.isConfirmed) return;

    try {
      await updateDoc(doc(db, 'units', event.unitId, 'events', event.id), {
        eventStatus: 'draft',
      });

      setEvent({ ...event, eventStatus: 'draft' });
      showSuccess('Event unpublished successfully!');
    } catch (error) {
      console.error('Error unpublishing event:', error);
      showError('Failed to unpublish event');
    }
  };

  const handleLockEvent = async () => {
    if (!event || (event.eventStatus !== 'published' && event.eventStatus !== 'interest-only')) return;

    const result = await confirmAction({
      title: 'Lock Event?',
      text:
        event.eventStatus === 'interest-only'
          ? 'This will stop any new interest registrations for this event.'
          : 'This will stop any new RSVPs for this event.',
      confirmButtonText: 'Lock Event',
    });

    if (!result.isConfirmed) return;

    try {
      await updateDoc(doc(db, 'units', event.unitId, 'events', event.id), {
        eventStatus: 'closed',
      });

      setEvent({ ...event, eventStatus: 'closed' });
      showSuccess('Event locked successfully. New RSVP submissions are now disabled.');
    } catch (error) {
      console.error('Error locking event:', error);
      showError('Failed to lock event');
    }
  };

  const handleUnlockEvent = async () => {
    if (!event || event.eventStatus !== 'closed') return;

    const result = await confirmAction({
      title: 'Unlock Event?',
      text: 'This will allow new RSVPs again.',
      confirmButtonText: 'Unlock Event',
    });

    if (!result.isConfirmed) return;

    try {
      await updateDoc(doc(db, 'units', event.unitId, 'events', event.id), {
        eventStatus: 'published',
      });

      setEvent({ ...event, eventStatus: 'published' });
      showSuccess('Event unlocked. RSVP submissions are enabled again.');
    } catch (error) {
      console.error('Error unlocking event:', error);
      showError('Failed to unlock event');
    }
  };

  const handleDeleteEvent = async () => {
    if (!event || event.eventStatus !== 'draft') return;

    const result = await confirmAction({
      title: 'Delete Event?',
      text: `Are you sure you want to delete "${event.name}"? This action cannot be undone. The event will be removed from the dashboard and hidden from users who have RSVP'd.`,
      confirmButtonText: 'Yes, delete it',
      icon: 'warning',
    });

    if (!result.isConfirmed) return;

    try {
      const eventRef = doc(db, 'units', event.unitId, 'events', event.id);
      
      // Soft delete: Update event status to 'deleted' instead of removing it
      await updateDoc(eventRef, {
        eventStatus: 'deleted',
      });

      showSuccess('Event deleted successfully!');
      navigate(`/unit/${event.unitId}/dashboard`);
    } catch (error) {
      console.error('Error deleting event:', error);
      showError('Failed to delete event. Please try again.');
    }
  };

  const handleCopyRSVPLink = async () => {
    if (!event) return;

    // If event has tickets, show modal to select which tickets to include
    if (event.tickets && event.tickets.length > 0) {
      setRsvpLinkModalOpen(true);
    } else {
      // No tickets, just copy the basic link
      const rsvpUrl = `${window.location.origin}/unit/${event.unitId}/event/${event.id}/rsvp`;
      try {
        await navigator.clipboard.writeText(rsvpUrl);
        showSuccess('RSVP link copied to clipboard!');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        showError(`Failed to copy. RSVP link: ${rsvpUrl}`);
      }
    }
  };

  const handleGeneratePlaceCards = () => {
    if (!displayEvent) {
      showError('Event not found');
      return;
    }

    navigate(`/unit/${displayEvent.unitId}/event/${eventId}/place-cards`);
  };

  const handleGenerateBookingForm = async () => {
    if (!displayEvent || rsvps.length === 0) {
      showError('No RSVPs found');
      return;
    }
    
    // Find the unit that owns this event
    const unitsSnapshot = await getDocs(collection(db, 'units'));
    let unitId = '';
    
    for (const unitDoc of unitsSnapshot.docs) {
      const eventDoc = await getDoc(
        doc(db, 'units', unitDoc.id, 'events', eventId!)
      );
      if (eventDoc.exists()) {
        unitId = unitDoc.id;
        break;
      }
    }

    if (unitId) {
      navigate(`/unit/${unitId}/event/${eventId}/kitchen-report`);
    } else {
      showError('Unable to find the unit for this event');
    }
  };

  const handleShowAttendanceList = () => {
    navigate(`/event/${eventId}/attendance`);
  };

  const handleInvoiceUpdatedFromModal = useCallback((inv: Invoice) => {
    setInvoiceMap((prev) => ({ ...prev, [inv.id]: inv }));
    setPaymentModalInvoice(inv);
    if (inv.status === 'paid' && inv.rsvps?.length) {
      const ids = new Set(inv.rsvps);
      setRsvps((prev) => prev.map((r) => (ids.has(r.id) ? { ...r, paid: true } : r)));
    }
  }, []);

  const openPaymentsModal = useCallback(
    async (rsvp: RSVP) => {
      if (!event) {
        return;
      }
      try {
        const payer = getInvoicePayerRsvp(rsvp, rsvps);
        const invId = payer.invoiceId?.trim();
        let inv: Invoice | null = invId ? invoiceMap[invId] ?? null : null;

        if (invId && !inv) {
          const snap = await getDoc(
            doc(db, 'units', event.unitId, 'events', event.id, 'invoices', invId),
          );
          if (snap.exists()) {
            const data = snap.data();
            if (data && isActiveInvoiceData(data)) {
              inv = mapFirestoreInvoiceData(snap.id, data as Record<string, unknown>);
              setInvoiceMap((prev) => ({ ...prev, [inv!.id]: inv! }));
            }
          }
        }

        if (!inv) {
          const result = await ensureInvoiceForBooking({
            event,
            rsvps,
            viewerRsvp: rsvp,
            ownerUid: userProfile?.uid,
          });
          if (!result.ok) {
            showError(result.message);
            return;
          }
          const createdInv = result.invoice;
          inv = createdInv;
          setInvoiceMap((prev) => ({ ...prev, [createdInv.id]: createdInv }));
          if (result.created) {
            setRsvps((prev) =>
              prev.map((row) =>
                createdInv.rsvps?.includes(row.id)
                  ? { ...row, invoiceId: createdInv.id }
                  : row,
              ),
            );
          }
        }

        if (!inv) {
          showError('No invoice could be loaded for this booking.');
          return;
        }

        setPaymentModalInvoice(inv);
        setPaymentModalOpen(true);
      } catch (e) {
        console.error(e);
        showError('Could not open payments.');
      }
    },
    [event, rsvps, invoiceMap, userProfile?.uid],
  );

  const handleExportCSV = async () => {
    if (!displayEvent || rsvps.length === 0) {
      showError('No RSVPs found');
      return;
    }

    const uid = displayEvent.unitId;
    if (uid) {
      try {
        const gate = httpsCallable(functions, 'assertUnitExportAllowed');
        await gate({ unitId: uid });
      } catch (err: unknown) {
        const details =
          err && typeof err === 'object' && 'details' in err
            ? (err as { details?: { code?: string } }).details
            : undefined;
        if (details?.code === 'CREDIT_DEBT') {
          showError(
            'Exports are paused until the unit credit balance is brought back to zero or above. Please ask your secretary or treasurer to top up.',
          );
          return;
        }
        console.error(err);
        showError('Could not verify export eligibility.');
        return;
      }
    }
    
    try {
      // Fetch unit to get province
      let unitProvince = '';
      if (displayEvent.unitId) {
        try {
          const unitDoc = await getDoc(doc(db, 'units', displayEvent.unitId));
          if (unitDoc.exists()) {
            unitProvince = unitDoc.data()?.province || '';
          }
        } catch (e) {
          console.warn('Failed to fetch unit province for CSV export', e);
        }
      }
      
      exportToCSV(rsvps, displayEvent, undefined, unitProvince, invoiceMap);
      showSuccess('CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      showError('Failed to export CSV');
    }
  };

  // Random data for generating test RSVPs
  const firstNames = TEST_FIRST_NAMES;
  const lastNames = TEST_LAST_NAMES;
  const additionalRanks = TEST_ADDITIONAL_RANKS;
  const unitNames = TEST_UNIT_NAMES;
  const allergies = TEST_ALLERGIES;

  const randomItem = <T,>(array: T[]): T => array[Math.floor(Math.random() * array.length)];

  const generateEmail = (firstName: string, lastName: string) => {
    const domains = TEST_EMAIL_DOMAINS;
    const random = Math.floor(Math.random() * 1000);
    return `${firstName.toLowerCase()}.${lastName.toLowerCase()}${random}@${randomItem(domains)}`;
  };

  const handleAddRandomRsvps = async () => {
    if (!event) return;
    
    const num = parseInt(numRandomRsvps);
    if (isNaN(num) || num <= 0 || num > 100) {
      showError('Please enter a number between 1 and 100');
      return;
    }

    const result = await confirmAction({
      title: 'Add Random RSVPs?',
      text: `This will create ${num} test RSVPs with randomized data. This action is for testing purposes only.`,
      confirmButtonText: 'Yes, Add RSVPs',
    });

    if (!result.isConfirmed) return;

    setGeneratingRsvps(true);
    setAddRandomRsvpsOpen(false);

    try {
      const transactionFee = 1.00;
      let confirmedCount = 0;
      let waitlistCount = 0;

      for (let i = 0; i < num; i++) {
        const firstName = randomItem(firstNames);
        const lastName = randomItem(lastNames);
        const email = generateEmail(firstName, lastName);
        // Determine rank options from admin-configured unit type ranks
        let rankOptions: string[] = [];
        try {
          const owningUnit = units.find((u) => u.id === event.unitId);
          if (owningUnit?.typeId) {
            const unitTypeSnap = await getDoc(doc(db, 'unitTypes', owningUnit.typeId));
            const ut = unitTypeSnap.exists() ? unitTypeSnap.data() as any : null;
            if (ut?.ranks && Array.isArray(ut.ranks) && ut.ranks.length > 0) {
              rankOptions = ut.ranks
                .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
                .map((r: any) => r.shortName || r.name)
                .filter(Boolean);
            }
          }
        } catch (e) {
          // Ignore and proceed without a generated prefix
        }
        const rank = rankOptions.length > 0 ? randomItem(rankOptions) : '';
        const additionalRank = randomItem(additionalRanks);
        const unit = randomItem(unitNames);
        const allergy = randomItem(allergies);

        // Build responses object
        const responses: Record<string, any> = {
          Allergies: allergy
        };

        // Determine ticket and pricing
        let ticketPrice = event.costPerPerson || 0;
        let selectedTicket: any = null;

        // If event has tickets, randomly select one and use its courses
        if (event.tickets && event.tickets.length > 0) {
          selectedTicket = randomItem(event.tickets);
          ticketPrice = selectedTicket.price || 0;

          // Always store ticket selection when tickets exist
          responses.Tickets = [{
            id: selectedTicket.id,
            name: selectedTicket.name,
            price: selectedTicket.price,
            qty: 1
          }];

          // Handle course selections from the selected ticket
          if (selectedTicket.courses && selectedTicket.courses.length > 0) {
            const courseData: Record<string, Record<string, Record<string, string>>> = {};
            courseData[selectedTicket.id] = {};
            // Instance index 1 for the first ticket (qty: 1)
            courseData[selectedTicket.id][1] = {};
            
            selectedTicket.courses.forEach((course: any) => {
              if (course.options && Array.isArray(course.options) && course.options.length > 0) {
                const selectedOption = randomItem(course.options) as string;
                // Store in the format: TicketCourses[ticketId][instanceIndex][courseId]
                courseData[selectedTicket.id][1][course.id] = selectedOption;
                // Also store in legacy format for compatibility
                responses[course.name] = selectedOption;
              }
            });
            
            // Only store TicketCourses if we have course selections
            if (Object.keys(courseData[selectedTicket.id][1]).length > 0) {
              responses.TicketCourses = courseData;
            }
          }
        } else if (event.courses && event.courses.length > 0) {
          // Fallback to event-level courses (legacy)
          event.courses.forEach((course) => {
            const selectedOption = randomItem(course.options);
            responses[course.name] = selectedOption;
          });
        } else {
          // Fallback to legacy meal choice
          responses.Meal = randomItem(TEST_MEAL_CHOICES);
        }

        // Determine status based on capacity
        let status: 'confirmed' | 'waitlist' = 'confirmed';
        if (event.maxCapacity && event.currentAttendees + confirmedCount >= event.maxCapacity) {
          status = 'waitlist';
        }

        const rsvpData: any = {
          eventId: event.id,
          firstName,
          lastName,
          name: `${firstName} ${lastName}`,
          email,
          rank,
          unit,
          responses,
          status,
          paid: false,
          checkedIn: false,
          plusOneAllowed: false,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };

        if (additionalRank) {
          rsvpData.additionalRank = additionalRank;
        }

        // Add RSVP to database
        const rsvpRef = await addDoc(
          collection(db, 'units', event.unitId, 'events', event.id, 'rsvps'),
          rsvpData
        );

        // Create invoice using ticket price
        const invoiceData = {
          eventId: event.id,
          unitId: event.unitId,
          ownerEmail: email,
          totalAmount: ticketPrice + transactionFee,
          amountPaid: 0,
          outstandingBalance: ticketPrice + transactionFee,
          status: 'outstanding',
          rsvps: [rsvpRef.id],
          paymentHistory: [],
          paymentLink: `${window.location.origin}/payment/${rsvpRef.id}`,
          remindersSent: [],
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };

        const invoiceRef = await addDoc(
          collection(db, 'units', event.unitId, 'events', event.id, 'invoices'),
          invoiceData
        );

        // Update RSVP with invoice ID
        await updateDoc(
          doc(db, 'units', event.unitId, 'events', event.id, 'rsvps', rsvpRef.id),
          { invoiceId: invoiceRef.id }
        );

        // currentAttendees is recounted server-side (syncEventAttendeeCount trigger).
        if (status === 'confirmed') {
          confirmedCount++;
        } else {
          waitlistCount++;
        }
      }

      // Refresh event data
      const eventDoc = await getDoc(doc(db, 'units', event.unitId, 'events', event.id));
      if (eventDoc.exists()) {
        const data = eventDoc.data();
        setEvent({
          ...event,
          currentAttendees: data.currentAttendees || 0
        });
      }

      // Refresh RSVPs
      const rsvpsSnapshot = await getDocs(
        collection(db, 'units', event.unitId, 'events', event.id, 'rsvps')
      );
      const rsvpData: RSVP[] = [];
      rsvpsSnapshot.forEach((doc) => {
        const rsvpDoc = doc.data();
        if (rsvpDoc.deletedAt) {
          return;
        }
        rsvpData.push({
          id: doc.id,
          eventId: event.id,
          userId: rsvpDoc.userId,
          name: rsvpDoc.name,
          firstName: rsvpDoc.firstName,
          lastName: rsvpDoc.lastName,
          rank: rsvpDoc.rank,
          additionalRank: rsvpDoc.additionalRank,
          unit: rsvpDoc.unit,
          email: rsvpDoc.email,
          responses: rsvpDoc.responses,
          status: rsvpDoc.status,
          invoiceId: rsvpDoc.invoiceId,
          paid: rsvpDoc.paid,
          invitedBy: rsvpDoc.invitedBy,
          checkedIn: rsvpDoc.checkedIn,
          checkInTime: rsvpDoc.checkInTime?.toDate(),
          specialRequests: rsvpDoc.specialRequests,
          plusOneAllowed: rsvpDoc.plusOneAllowed,
          createdAt: rsvpDoc.createdAt?.toDate() || new Date(),
          updatedAt: rsvpDoc.updatedAt?.toDate() || new Date(),
        });
      });
      setRsvps(rsvpData);

      showSuccess(`Successfully created ${num} test RSVPs! (${confirmedCount} confirmed, ${waitlistCount} waitlist)`);
      setNumRandomRsvps('10'); // Reset input
    } catch (error) {
      console.error('Error adding random RSVPs:', error);
      showError('Failed to add random RSVPs. Please try again.');
    } finally {
      setGeneratingRsvps(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading event...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Unable to Load Event</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2">
              <Button onClick={() => window.location.reload()}>Retry</Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Event Not Found</CardTitle>
              <CardDescription>The event you're looking for doesn't exist or you don't have access.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const displayEvent = event;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 w-full min-w-0 overflow-x-hidden py-6 sm:py-8">
        <DashboardContentContainer>
          <div className="mb-6 sm:mb-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 h-auto max-w-full whitespace-normal px-2 text-left text-sm sm:text-base"
              onClick={() => navigate(`/unit/${displayEvent.unitId}/dashboard`)}
            >
              ← Back to Unit Dashboard
            </Button>
            <EventHeaderCredits unitId={displayEvent.unitId} />
          </div>

          {isAdminViewing && (
            <Alert variant="warning" className="mb-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
                <Shield className="h-5 w-5 mt-0.5 shrink-0" />
                <AlertDescription>
                  <strong>Admin Access:</strong> You are viewing this event dashboard using administrator privileges. You are not a member of this unit, so some actions may be restricted based on unit-specific permissions.
                </AlertDescription>
              </div>
            </Alert>
          )}
          {displayEvent.eventStatus === 'closed' && (
            <Alert variant="warning" className="mb-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
                <Lock className="h-5 w-5 mt-0.5 shrink-0" />
                <AlertDescription>
                  <strong>Event Locked:</strong> This event is locked and no new RSVP submissions are allowed.
                </AlertDescription>
              </div>
            </Alert>
          )}

          {/* Event Header */}
          <div className="mb-6 flex flex-col gap-4 sm:mb-8 md:flex-row md:items-start md:justify-between md:gap-6">
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-3">
                <h1 className="break-words text-2xl font-bold sm:text-3xl">{displayEvent.name}</h1>
                <Badge
                  variant={displayEvent.eventStatus === 'published' ? 'success' : 'warning'}
                  className="w-fit shrink-0 text-sm"
                >
                  {formatEventStatusLabel(displayEvent.eventStatus)}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground sm:text-base">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 shrink-0" aria-hidden />
                  {formatDate(displayEvent.date)}
                </span>
                <span className="text-muted-foreground/70" aria-hidden>
                  •
                </span>
                <span className="inline-flex min-w-0 items-start gap-1.5 break-words">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  {displayEvent.location}
                </span>
              </div>
            </div>
            <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center md:w-auto md:max-w-none md:justify-end">
              {displayEvent.eventStatus === 'draft' && (
                <div className="flex flex-wrap gap-2">
                  <Button className="min-h-10 flex-1 sm:flex-none" onClick={handlePublishEvent}>
                    Publish Event
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="min-h-9"
                    onClick={handleDeleteEvent}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Event
                  </Button>
                </div>
              )}
              {displayEvent.eventStatus === 'interest-only' && (
                <div className="flex flex-wrap gap-2">
                  <Button className="min-h-10 flex-1 sm:flex-none" onClick={handlePublishEvent}>
                    Upgrade to Full Event
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto min-h-10 flex-1 whitespace-normal py-2 sm:flex-none"
                    onClick={handleCopyRSVPLink}
                  >
                    <Copy className="mr-2 h-4 w-4 shrink-0" />
                    Copy Interest Link
                  </Button>
                  <Button variant="destructive" size="sm" className="min-h-9 w-full sm:w-auto" onClick={handleUnpublishEvent}>
                    Unpublish Event
                  </Button>
                  <Button variant="outline" size="sm" className="min-h-9 w-full sm:w-auto" onClick={handleLockEvent}>
                    <Lock className="mr-2 h-4 w-4" />
                    Lock Event
                  </Button>
                </div>
              )}
              {displayEvent.eventStatus === 'published' && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    className="h-auto min-h-10 flex-1 whitespace-normal py-2 sm:flex-none"
                    onClick={handleCopyRSVPLink}
                  >
                    <Copy className="mr-2 h-4 w-4 shrink-0" />
                    Copy RSVP Link
                  </Button>
                  <Button variant="destructive" size="sm" className="min-h-9 w-full sm:w-auto" onClick={handleUnpublishEvent}>
                    Unpublish Event
                  </Button>
                  <Button variant="outline" size="sm" className="min-h-9 w-full sm:w-auto" onClick={handleLockEvent}>
                    <Lock className="mr-2 h-4 w-4" />
                    Lock Event
                  </Button>
                </div>
              )}
              {displayEvent.eventStatus === 'closed' && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="min-h-9 w-full sm:w-auto" onClick={handleUnlockEvent}>
                    <Unlock className="mr-2 h-4 w-4" />
                    Unlock Event
                  </Button>
                  <Button variant="destructive" size="sm" className="min-h-9 w-full sm:w-auto" onClick={handleUnpublishEvent}>
                    Unpublish Event
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:mb-8 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-6">
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={() => navigate(`/unit/${displayEvent.unitId}/event/${eventId}/table-planner`)}
              aria-label="Open Table Planner"
            >
              <LayoutGrid className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Table Planner</span>
            </Button>
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={handleGeneratePlaceCards}
              aria-label="Generate Place Cards"
            >
              <Printer className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Place Cards</span>
            </Button>
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={() => navigate(`/unit/${displayEvent.unitId}/event/${eventId}/check-in`)}
              aria-label="Go to Check-In"
            >
              <CheckCircle className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Check-In</span>
            </Button>
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={() => navigate(`/unit/${displayEvent.unitId}/event/${eventId}/announcements`)}
              aria-label="Open Announcements"
            >
              <Bell className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Announcements</span>
            </Button>
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={handleShowAttendanceList}
              aria-label="Open Attendance List"
            >
              <ClipboardList className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Attendance List</span>
            </Button>
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={() => navigate(`/event/${eventId}/settings`)}
              aria-label="Open Settings"
            >
              <Settings className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Settings</span>
            </Button>
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={handleGenerateBookingForm}
              aria-label="Booking Form"
            >
              <ChefHat className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Booking Form</span>
            </Button>
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={() => navigate(`/unit/${displayEvent.unitId}/event/${eventId}/analytics`)}
              aria-label="Analytics"
            >
              <BarChart3 className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Analytics</span>
            </Button>
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={() => navigate(`/unit/${displayEvent.unitId}/event/${eventId}/payments`)}
              disabled={!event}
              aria-label="Payments"
            >
              <FileText className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Payments</span>
            </Button>
            <Button
              variant="outline"
              className="min-h-20 h-auto flex-col gap-1 px-2 py-3 text-xs leading-tight sm:h-20 sm:py-0 sm:text-sm"
              onClick={handleExportCSV}
              aria-label="Export CSV"
            >
              <Download className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
              <span className="text-center">Export CSV</span>
            </Button>
            {userProfile?.isAdmin && import.meta.env.DEV && (
              <Button
                variant="outline"
                className="min-h-20 h-auto flex-col gap-1 border-amber-300 px-2 py-3 text-xs leading-tight text-foreground hover:bg-amber-50 hover:text-amber-950 dark:border-amber-500/50 dark:hover:bg-amber-950/70 dark:hover:text-amber-50 sm:h-20 sm:py-0 sm:text-sm"
                onClick={() => setAddRandomRsvpsOpen(true)}
                disabled={generatingRsvps}
                aria-label="Add Test RSVPs"
              >
                <UserPlus className="h-5 w-5 shrink-0 sm:mb-2 sm:h-6 sm:w-6" />
                <span className="text-center">{generatingRsvps ? 'Generating...' : 'Add Test RSVPs'}</span>
              </Button>
            )}
          </div>

          {/* Stats Cards */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:mb-8 sm:gap-4 md:gap-6 lg:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {displayEvent.eventStatus === 'interest-only' ? 'Interest Registrations' : 'Attendees'}
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {displayEvent.eventStatus === 'interest-only'
                    ? rsvps.length
                    : attendeeStatSummary.attending}
                </div>
                {displayEvent.eventStatus !== 'interest-only' && (
                  <p className="text-xs text-muted-foreground">
                    of {attendeeStatSummary.total} RSVPs
                    {attendeeStatSummary.apologyCount > 0 &&
                      ` (${attendeeStatSummary.apologyCount} ${attendeeStatSummary.apologyCount === 1 ? 'apology' : 'apologies'})`}
                  </p>
                )}
                {displayEvent.eventStatus === 'interest-only' && (
                  <p className="text-xs text-muted-foreground">
                    people interested
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {displayEvent.eventStatus === 'interest-only' ? 'Event Status' : 'Revenue'}
                </CardTitle>
                <PoundSterling className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {displayEvent.eventStatus === 'interest-only' ? (
                  <>
                    <div className="text-2xl font-bold">Planning</div>
                    <p className="text-xs text-muted-foreground">Finalizing details</p>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold">{formatCurrency(revenueStats.totalRevenue)}</div>
                    <p className="text-xs text-muted-foreground">
                      {revenueStats.averagePrice > 0
                        ? `${formatCurrency(revenueStats.averagePrice)} avg per person`
                        : 'No pricing set'}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {displayEvent.eventStatus === 'interest-only' ? 'Next Step' : 'Payments Collected'}
                </CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {displayEvent.eventStatus === 'interest-only' ? (
                  <>
                    <div className="text-xl font-bold">Publish Event</div>
                    <p className="text-xs text-muted-foreground">to collect RSVPs</p>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold">{formatCurrency(revenueStats.totalCollected)}</div>
                    <p className="text-xs text-muted-foreground">
                      {revenueStats.paidRsvpCount} attendee{revenueStats.paidRsvpCount !== 1 ? 's' : ''} settled
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {displayEvent.eventStatus === 'interest-only' ? 'Mode' : 'Outstanding'}
                </CardTitle>
                <PoundSterling className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {displayEvent.eventStatus === 'interest-only' ? (
                  <>
                    <div className="text-xl font-bold">Interest Only</div>
                    <p className="text-xs text-muted-foreground">No payments yet</p>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold">
                      {formatCurrency(revenueStats.totalOutstanding)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {revenueStats.unpaidRsvpCount} unpaid attendee{revenueStats.unpaidRsvpCount !== 1 ? 's' : ''}
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {displayEvent.eventStatus === 'interest-only' ? 'Awaiting' : 'Payments Pending'}
                </CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {displayEvent.eventStatus === 'interest-only' ? (
                  <>
                    <div className="text-xl font-bold">—</div>
                    <p className="text-xs text-muted-foreground">No payments yet</p>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold">
                      {formatCurrency(revenueStats.totalPending)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {revenueStats.pendingRsvpCount} awaiting approval
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Event Details & RSVP List */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="min-w-0 lg:col-span-2">
              <Card>
                <CardHeader className="space-y-1 px-4 sm:px-6">
                  <CardTitle className="text-lg sm:text-xl">
                    {displayEvent.eventStatus === 'interest-only' ? 'Waitlist' : 'Recent RSVPs'}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {displayEvent.eventStatus === 'interest-only' 
                      ? 'People on the waitlist for this event' 
                      : 'Latest registrations for this event'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-3 sm:px-6">
                  {displayedRsvps.length > 0 && (
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
                      <span className="text-xs text-muted-foreground sm:shrink-0">Sort by:</span>
                      <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                        <SelectTrigger className="h-9 w-full sm:h-8 sm:w-40">
                          <SelectValue placeholder="Recent" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="recent">Recent</SelectItem>
                          <SelectItem value="name">Name</SelectItem>
                          <SelectItem value="payment">Payment Status</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {displayedRsvps.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>
                        {displayEvent.eventStatus === 'interest-only' 
                          ? 'No one on waitlist yet' 
                          : 'No RSVPs yet'}
                      </p>
                      <p className="text-sm mt-2">
                        {displayEvent.eventStatus === 'interest-only'
                          ? 'Share the interest link to start collecting waitlist registrations'
                          : 'Share the RSVP link to start collecting registrations'}
                      </p>
                      <div className="mt-4">
                        <Button
                          variant="outline"
                          className="h-auto min-h-10 w-full max-w-sm whitespace-normal py-2 sm:w-auto"
                          onClick={handleCopyRSVPLink}
                          aria-label="Copy RSVP link from empty state"
                        >
                          <Copy className="mr-2 h-4 w-4 shrink-0" /> Copy Link
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3 md:hidden">
                        {displayedRsvps.slice(0, 5).map((rsvp) => {
                          const row = buildRsvpDashboardRowModel(rsvp, displayEvent);
                          const paymentState = getAttendanceRsvpPaymentState(
                            rsvp,
                            rsvps,
                            displayEvent,
                            invoiceMap,
                          );
                          const displayPaid = paymentState.displayPaid;

                          return (
                            <Card key={rsvp.id}>
                              <CardContent className="space-y-3 p-4">
                                <div className="min-w-0">
                                  <div className="flex items-start gap-1.5 font-medium">
                                    <span className="min-w-0 break-words">
                                      {rsvp.rank && `${rsvp.rank} `}
                                      {rsvp.name}
                                    </span>
                                    {rsvp.userId && typeof rsvp.userId === 'string' && rsvp.userId.trim().length > 0 && (
                                      <span title="Has account on Masonic Tools">
                                        <User className="h-4 w-4 shrink-0 text-blue-600" />
                                      </span>
                                    )}
                                  </div>
                                  <p className="mt-1 break-all text-xs text-muted-foreground">{rsvp.email}</p>
                                  {row.hasMeaningfulNote && (
                                    <p className="mt-1 text-xs text-muted-foreground">Note: {rsvp.specialRequests}</p>
                                  )}
                                </div>
                                {displayEvent.eventStatus !== 'interest-only' && (
                                  <p className="text-xs text-muted-foreground" title={row.mealTooltip}>
                                    <span className="font-medium text-foreground">
                                      {isMultiTenantEvent(displayEvent) ? 'Tickets:' : 'Meal:'}
                                    </span>{' '}
                                    <span className="break-words">{row.compactMealSummary}</span>
                                  </p>
                                )}
                                <p
                                  className="text-xs text-muted-foreground"
                                  title={row.unitDisplay !== '-' ? row.unitDisplay : undefined}
                                >
                                  <span className="font-medium text-foreground">Unit:</span> {row.unitTruncated}
                                </p>
                                <div className="flex flex-col gap-3 border-t pt-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    {displayEvent.eventStatus === 'interest-only' && (
                                      <Badge variant="secondary">On Waitlist</Badge>
                                    )}
                                    {row.isInterestOnly ? (
                                      <Badge variant="outline">N/A</Badge>
                                    ) : rsvp.apologies ? (
                                      <Badge variant="secondary" className="border-amber-200 bg-amber-100 text-amber-800">
                                        Apologies
                                      </Badge>
                                    ) : (
                                      <Badge variant={displayPaid ? 'success' : 'warning'}>
                                        {displayPaid ? 'Paid' : 'Unpaid'}
                                      </Badge>
                                    )}
                                  </div>
                                  {displayEvent.eventStatus !== 'interest-only' && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-9 w-full"
                                      title="View payments"
                                      onClick={() => openPaymentsModal(rsvp)}
                                    >
                                      <Wallet className="mr-2 h-4 w-4" aria-hidden />
                                      View payments
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                      <div className="-mx-1 hidden overflow-x-auto overscroll-x-contain px-1 md:block sm:mx-0 sm:px-0">
                        <table className="w-full min-w-[20rem] text-sm sm:min-w-0">
                          <caption className="sr-only">Recent RSVPs table</caption>
                          <thead className="border-b">
                            <tr className="text-left text-xs text-muted-foreground">
                              <th scope="col" className="min-w-[7rem] pb-2 pr-2 font-semibold">Name</th>
                              <th scope="col" className="hidden pb-2 pr-2 font-semibold sm:table-cell">Email</th>
                              <th scope="col" className="hidden pb-2 pr-2 font-semibold md:table-cell">Unit</th>
                              {displayEvent.eventStatus !== 'interest-only' && (
                                <th scope="col" className="hidden pb-2 pr-2 font-semibold lg:table-cell">
                                  {isMultiTenantEvent(displayEvent) ? 'Tickets' : 'Meal'}
                                </th>
                              )}
                              {displayEvent.eventStatus === 'interest-only' && (
                                <th scope="col" className="pb-2 pr-2 font-semibold">Status</th>
                              )}
                              <th scope="col" className="pb-2 pr-2 font-semibold">Payment</th>
                              {displayEvent.eventStatus !== 'interest-only' && (
                                <th scope="col" className="pb-2 pl-1 text-right font-semibold sm:pl-2">Payments</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {displayedRsvps.slice(0, 5).map((rsvp) => {
                              const row = buildRsvpDashboardRowModel(rsvp, displayEvent);

                              return (
                                <tr key={rsvp.id} className="border-b last:border-0">
                                  <td className="max-w-[11rem] py-2 sm:max-w-none">
                                    <div className="flex items-center gap-1.5 font-medium">
                                      <span className="min-w-0 break-words">
                                        {rsvp.rank && `${rsvp.rank} `}{rsvp.name}
                                      </span>
                                      {rsvp.userId && typeof rsvp.userId === 'string' && rsvp.userId.trim().length > 0 && (
                                        <span title="Has account on Masonic Tools">
                                          <User className="h-4 w-4 shrink-0 text-blue-600" />
                                        </span>
                                      )}
                                    </div>
                                    <div className="mt-1 text-xs text-muted-foreground sm:hidden">{rsvp.email}</div>
                                    {row.hasMeaningfulNote && (
                                      <div className="mt-0.5 text-xs text-muted-foreground">
                                        Note: {rsvp.specialRequests}
                                      </div>
                                    )}
                                  </td>
                                  <td className="hidden py-2 text-xs text-muted-foreground sm:table-cell">{rsvp.email}</td>
                                  <td
                                    className="hidden py-2 text-xs text-muted-foreground md:table-cell"
                                    title={row.unitDisplay !== '-' ? row.unitDisplay : undefined}
                                  >
                                    {row.unitTruncated}
                                  </td>
                                  {displayEvent.eventStatus !== 'interest-only' && (
                                    <td className="hidden py-2 lg:table-cell">
                                      <span className="text-xs text-muted-foreground" title={row.mealTooltip}>
                                        {row.compactMealSummary}
                                      </span>
                                    </td>
                                  )}
                                  {displayEvent.eventStatus === 'interest-only' && (
                                    <td className="py-2">
                                      <Badge variant="secondary">On Waitlist</Badge>
                                    </td>
                                  )}
                                  <td className="py-2">
                                    {row.isInterestOnly ? (
                                      <Badge variant="outline">N/A</Badge>
                                    ) : rsvp.apologies ? (
                                      <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">
                                        Apologies
                                      </Badge>
                                    ) : (
                                      (() => {
                                        const displayPaidRow = getAttendanceRsvpPaymentState(
                                          rsvp,
                                          rsvps,
                                          displayEvent,
                                          invoiceMap,
                                        ).displayPaid;
                                        return (
                                          <Badge variant={displayPaidRow ? 'success' : 'warning'}>
                                            {displayPaidRow ? 'Paid' : 'Unpaid'}
                                          </Badge>
                                        );
                                      })()
                                    )}
                                  </td>
                                  {displayEvent.eventStatus !== 'interest-only' && (
                                    <td className="py-2 text-right align-middle">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-9 min-w-9 px-2 sm:h-8"
                                        title="View payments"
                                        onClick={() => openPaymentsModal(rsvp)}
                                      >
                                        <Wallet className="h-4 w-4" aria-hidden />
                                        <span className="sr-only">View payments</span>
                                      </Button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {displayedRsvps.length > 5 && (
                        <div className="text-center text-sm text-muted-foreground pt-4 border-t mt-4">
                          <p className="pt-3">+ {displayedRsvps.length - 5} more {displayEvent.eventStatus === 'interest-only' ? 'on waitlist' : 'RSVPs'}</p>
                          <Button 
                            variant="link" 
                            className="mt-2"
                            onClick={handleShowAttendanceList}
                          >
                            View Complete Attendance List
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="min-w-0">
              <Card className="mb-6">
                <CardHeader className="px-4 sm:px-6">
                  <CardTitle className="text-lg sm:text-xl">Event Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 px-4 text-sm sm:px-6">
                  <div>
                    <div className="font-semibold">Date & Time</div>
                    <div className="text-muted-foreground">{formatDate(displayEvent.date)}</div>
                  </div>
                  <div>
                    <div className="font-semibold">Location</div>
                    <div className="text-muted-foreground">{displayEvent.location}</div>
                  </div>
                  <div>
                    <div className="font-semibold">Dress Code</div>
                    <div className="text-muted-foreground">{displayEvent.dresscode || 'Not specified'}</div>
                  </div>
                  <div>
                    <div className="font-semibold">RSVP Deadline</div>
                    <div className={`text-muted-foreground ${new Date(displayEvent.rsvpDeadline) < new Date() ? 'text-red-600' : ''}`}>{formatDate(displayEvent.rsvpDeadline)}</div>
                  </div>
                  <div>
                    <div className="font-semibold">Payment Deadline</div>
                    <div className={`text-muted-foreground ${displayEvent.paymentDeadline && new Date(displayEvent.paymentDeadline) < new Date() ? 'text-red-600' : ''}`}>{displayEvent.paymentDeadline ? formatDate(displayEvent.paymentDeadline) : 'Not specified'}</div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </DashboardContentContainer>
      </main>

      {/* Publish Event Modal */}
      {displayEvent && displayEvent.eventStatus !== 'deleted' && (
        <PublishEventModal
          open={publishModalOpen}
          onOpenChange={setPublishModalOpen}
          eventId={displayEvent.id}
          unitId={displayEvent.unitId}
          eventName={displayEvent.name}
          eventStatus={displayEvent.eventStatus as 'draft' | 'interest-only' | 'published' | 'closed' | 'completed'}
          onPublished={handleEventPublished}
        />
      )}

      {/* Add Random RSVPs Dialog */}
      {userProfile?.isAdmin && import.meta.env.DEV && (
      <Dialog open={addRandomRsvpsOpen} onOpenChange={setAddRandomRsvpsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Test RSVPs</DialogTitle>
            <DialogDescription>
              Generate random test RSVPs with realistic data for testing purposes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="num-rsvps">Number of RSVPs (1-100)</Label>
              <Input
                id="num-rsvps"
                type="number"
                min="1"
                max="100"
                value={numRandomRsvps}
                onChange={(e) => setNumRandomRsvps(e.target.value)}
                placeholder="10"
              />
            </div>

            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
              <div className="flex items-start gap-2">
                <UserPlus className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-amber-900 mb-1">Admin Testing Feature</p>
                  <p className="text-amber-800">
                    This will create test RSVPs with:
                  </p>
                  <ul className="list-disc list-inside text-amber-800 mt-2 space-y-1">
                    <li>Random names and emails</li>
                    <li>Random ranks and units</li>
                    <li>Random meal selections</li>
                    <li>Complete invoices</li>
                    <li>Respect capacity limits</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddRandomRsvpsOpen(false)}
              disabled={generatingRsvps}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddRandomRsvps}
              disabled={generatingRsvps}
            >
              {generatingRsvps ? 'Generating...' : 'Add RSVPs'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* RSVP Link Modal */}
      {event && (
        <RSVPLinkModal
          open={rsvpLinkModalOpen}
          onOpenChange={setRsvpLinkModalOpen}
          eventId={event.id}
          unitId={event.unitId}
          tickets={event.tickets || []}
        />
      )}

      <InvoicePaymentsModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        event={event}
        invoice={paymentModalInvoice}
        rsvps={rsvps}
        onInvoiceUpdated={handleInvoiceUpdatedFromModal}
      />
    </div>
  );
}

